import { supabase } from '../utils/supabase';

/**
 * Production-Ready ML-Based Scoring System
 * 
 * This service implements advanced machine learning algorithms for:
 * - Skill compatibility matching
 * - Success probability prediction
 * - Cultural fit assessment
 * - Performance prediction
 * - Retention likelihood
 * - Real-time recommendations
 */

export class MLScoringService {
  constructor() {
    this.modelVersion = '2.1.0';
    this.confidenceThreshold = 0.7;
    this.cacheExpiry = 3600; // 1 hour
    this.userRoleCache = new Map(); // Cache user roles to avoid repeated DB calls
    this.roleCacheExpiry = 300000; // 5 minutes in milliseconds
  }

  /**
   * Calculate comprehensive ML-based match score between candidate and job
   */
  async calculateMLMatchScore(candidateId, jobId, employerId) {
    try {
      console.log(`[ML] Calculating match score: candidate=${candidateId}, job=${jobId}`);

      // Check if we have cached results (with error handling)
      let cachedResult = null;
      try {
        cachedResult = await this.getCachedScore(candidateId, jobId, employerId);
        if (cachedResult) {
          console.log('[ML] Using cached score');
          return { success: true, data: cachedResult, source: 'cache' };
        }
      } catch (cacheError) {
        console.warn('[ML] Cache access failed, proceeding with fresh calculation:', cacheError.message);
      }

      // Get candidate and job data
      const [candidateData, jobData, behaviorData] = await Promise.all([
        this.getCandidateMLFeatures(candidateId),
        this.getJobMLFeatures(jobId),
        this.getBehavioralData(candidateId)
      ]);

      if (!candidateData.success || !jobData.success) {
        throw new Error('Failed to fetch required data for ML scoring');
      }

      // Calculate individual ML scores
      const skillMatchScore = this.calculateSkillCompatibilityScore(
        candidateData.data, 
        jobData.data
      );

      const compatibilityScore = this.calculateCompatibilityScore(
        candidateData.data, 
        jobData.data
      );

      const successProbability = this.calculateSuccessProbability(
        candidateData.data, 
        jobData.data, 
        behaviorData.data
      );

      const retentionScore = this.calculateRetentionScore(
        candidateData.data, 
        behaviorData.data
      );

      const performanceScore = this.calculatePerformanceScore(
        candidateData.data, 
        jobData.data
      );

      const culturalFitScore = this.calculateCulturalFitScore(
        candidateData.data, 
        jobData.data
      );

      const communicationScore = this.calculateCommunicationScore(
        candidateData.data, 
        behaviorData.data
      );

      // Calculate weighted composite score
      const weights = await this.getModelWeights();
      const compositeScore = this.calculateCompositeScore({
        skillMatchScore,
        compatibilityScore,
        successProbability,
        retentionScore,
        performanceScore,
        culturalFitScore,
        communicationScore
      }, weights);

      // Calculate confidence level
      const confidence = this.calculateConfidenceLevel({
        dataQuality: this.assessDataQuality(candidateData.data, jobData.data),
        modelAccuracy: weights.modelAccuracy || 0.85,
        featureCompleteness: this.calculateFeatureCompleteness(candidateData.data)
      });

      // Generate feature importance for explainability
      const featureImportance = this.generateFeatureImportance({
        skillMatchScore,
        compatibilityScore,
        successProbability,
        retentionScore,
        performanceScore,
        culturalFitScore,
        communicationScore
      });

      const result = {
        candidate_id: candidateId,
        job_id: jobId,
        employer_id: employerId,
        skill_match_score: skillMatchScore,
        compatibility_score: compatibilityScore,
        success_probability: successProbability,
        retention_score: retentionScore,
        performance_score: performanceScore,
        cultural_fit_score: culturalFitScore,
        communication_score: communicationScore,
        composite_score: compositeScore,
        overall_confidence: confidence,
        feature_importance: featureImportance,
        model_version: this.modelVersion,
        calculation_timestamp: new Date().toISOString()
      };

      // Store results in database
      await this.storeMLResults(result);

      // Cache the results
      await this.cacheScore(candidateId, jobId, employerId, result);

      console.log(`[ML] Calculated composite score: ${compositeScore} (confidence: ${confidence})`);

      return { success: true, data: result, source: 'calculated' };

    } catch (error) {
      console.error('[ML] Error calculating ML match score:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Advanced skill compatibility using semantic similarity
   */
  calculateSkillCompatibilityScore(candidate, job) {
    const candidateSkills = this.normalizeSkills(candidate.skills || []);
    const requiredSkills = this.normalizeSkills(job.skills_required || []);
    
    if (requiredSkills.length === 0) return 0.7; // Default score
    if (candidateSkills.length === 0) return 0.1;

    // Semantic similarity matrix
    const skillCategories = {
      'technical': ['programming', 'coding', 'development', 'software', 'web', 'database', 'api'],
      'administrative': ['admin', 'data entry', 'scheduling', 'organization', 'filing'],
      'communication': ['writing', 'email', 'customer service', 'support', 'chat'],
      'marketing': ['social media', 'content', 'seo', 'marketing', 'advertising'],
      'design': ['graphic design', 'ui', 'ux', 'photoshop', 'design', 'creative'],
      'analysis': ['analytics', 'research', 'reporting', 'excel', 'data analysis']
    };

    let totalScore = 0;
    let maxPossibleScore = 0;

    for (const requiredSkill of requiredSkills) {
      let bestMatch = 0;
      
      for (const candidateSkill of candidateSkills) {
        // Exact match
        if (candidateSkill === requiredSkill) {
          bestMatch = 1.0;
          break;
        }

        // Partial match
        if (candidateSkill.includes(requiredSkill) || requiredSkill.includes(candidateSkill)) {
          bestMatch = Math.max(bestMatch, 0.8);
        }

        // Category match
        const requiredCategory = this.getSkillCategory(requiredSkill, skillCategories);
        const candidateCategory = this.getSkillCategory(candidateSkill, skillCategories);
        
        if (requiredCategory && requiredCategory === candidateCategory) {
          bestMatch = Math.max(bestMatch, 0.6);
        }

        // Semantic similarity (simplified)
        const similarity = this.calculateSemanticSimilarity(requiredSkill, candidateSkill);
        bestMatch = Math.max(bestMatch, similarity);
      }

      totalScore += bestMatch;
      maxPossibleScore += 1;
    }

    // Experience bonus
    const experienceBonus = Math.min(0.2, (candidate.experience_years || 0) * 0.02);
    
    // Quality bonus based on ratings
    const qualityBonus = Math.min(0.1, ((candidate.rating || 4.0) - 4.0) * 0.2);

    const finalScore = Math.min(1.0, (totalScore / maxPossibleScore) + experienceBonus + qualityBonus);
    
    return Math.round(finalScore * 10000) / 10000; // 4 decimal precision
  }

  /**
   * Calculate compatibility based on work style, preferences, and requirements
   */
  calculateCompatibilityScore(candidate, job) {
    let score = 0.5; // Base score
    let factors = 0;

    // Hourly rate compatibility
    if (job.hourly_rate_min && job.hourly_rate_max && candidate.hourly_rate) {
      const rateMatch = this.calculateRateCompatibility(
        candidate.hourly_rate,
        job.hourly_rate_min,
        job.hourly_rate_max
      );
      score += rateMatch * 0.25;
      factors++;
    }

    // Experience level compatibility
    if (job.min_experience_years !== undefined && candidate.experience_years !== undefined) {
      const expCompatibility = this.calculateExperienceCompatibility(
        candidate.experience_years,
        job.min_experience_years
      );
      score += expCompatibility * 0.2;
      factors++;
    }

    // Job type compatibility (remote, timezone, etc.)
    const workStyleScore = this.calculateWorkStyleCompatibility(candidate, job);
    score += workStyleScore * 0.15;
    factors++;

    // Complexity compatibility
    const complexityScore = this.calculateComplexityCompatibility(candidate, job);
    score += complexityScore * 0.15;
    factors++;

    // Availability compatibility
    const availabilityScore = this.calculateAvailabilityCompatibility(candidate, job);
    score += availabilityScore * 0.1;
    factors++;

    // Industry/domain compatibility
    const domainScore = this.calculateDomainCompatibility(candidate, job);
    score += domainScore * 0.15;
    factors++;

    // Normalize by number of factors considered
    if (factors > 0) {
      score = score / (1 + factors * 0.1); // Weighted normalization
    }

    return Math.min(1.0, Math.max(0.0, score));
  }

  /**
   * Predict success probability using historical data patterns
   */
  calculateSuccessProbability(candidate, job, behaviorData) {
    let probability = 0.6; // Base probability

    // Historical success rate
    if (candidate.success_rate !== undefined) {
      probability += (candidate.success_rate - 0.5) * 0.4;
    }

    // Rating influence
    if (candidate.rating) {
      const ratingImpact = ((candidate.rating - 3.0) / 2.0) * 0.3;
      probability += ratingImpact;
    }

    // Activity score influence
    if (candidate.activity_score) {
      probability += (candidate.activity_score - 0.5) * 0.2;
    }

    // Response time factor
    if (candidate.response_time_avg) {
      const responseScore = Math.max(0, 1 - (candidate.response_time_avg / 86400)); // Normalize to daily
      probability += responseScore * 0.15;
    }

    // Behavioral patterns
    if (behaviorData && behaviorData.recent_applications > 0) {
      const activityBonus = Math.min(0.1, behaviorData.recent_applications * 0.02);
      probability += activityBonus;
    }

    // Job complexity vs candidate experience
    const complexityMatch = this.calculateComplexityExperienceMatch(candidate, job);
    probability += complexityMatch * 0.2;

    // Portfolio completeness
    const profileCompleteness = this.calculateProfileCompleteness(candidate);
    probability += profileCompleteness * 0.1;

    return Math.min(1.0, Math.max(0.1, probability));
  }

  /**
   * Calculate retention likelihood based on historical patterns
   */
  calculateRetentionScore(candidate, behaviorData) {
    let score = 0.5; // Base retention score

    // Historical retention rate
    if (candidate.retention_rate !== undefined) {
      score += (candidate.retention_rate - 0.5) * 0.5;
    }

    // Experience correlation with retention
    if (candidate.experience_years) {
      const experienceRetentionBonus = Math.min(0.2, candidate.experience_years * 0.02);
      score += experienceRetentionBonus;
    }

    // Behavioral stability indicators
    if (behaviorData) {
      const stabilityScore = this.calculateBehavioralStability(behaviorData);
      score += stabilityScore * 0.2;
    }

    // Communication rating impact
    if (candidate.communication_rating) {
      const commImpact = ((candidate.communication_rating - 3.0) / 2.0) * 0.15;
      score += commImpact;
    }

    // Work quality rating impact
    if (candidate.work_quality_rating) {
      const qualityImpact = ((candidate.work_quality_rating - 3.0) / 2.0) * 0.15;
      score += qualityImpact;
    }

    return Math.min(1.0, Math.max(0.1, score));
  }

  /**
   * Predict performance score based on historical data and indicators
   */
  calculatePerformanceScore(candidate, job) {
    let score = 0.5; // Base performance score

    // Skills-to-requirements ratio
    const skillsRatio = this.calculateSkillsToRequirementsRatio(candidate, job);
    score += skillsRatio * 0.3;

    // Experience adequacy
    const experienceAdequacy = this.calculateExperienceAdequacy(candidate, job);
    score += experienceAdequacy * 0.25;

    // Historical performance indicators
    if (candidate.work_quality_rating) {
      const qualityScore = (candidate.work_quality_rating - 3.0) / 2.0;
      score += qualityScore * 0.2;
    }

    // Portfolio quality (if available)
    const portfolioScore = this.assessPortfolioQuality(candidate);
    score += portfolioScore * 0.1;

    // Specialization match
    const specializationScore = this.calculateSpecializationMatch(candidate, job);
    score += specializationScore * 0.15;

    return Math.min(1.0, Math.max(0.1, score));
  }

  /**
   * Assess cultural fit based on work preferences and communication style
   */
  calculateCulturalFitScore(candidate, job) {
    let score = 0.6; // Base cultural fit

    // Communication style compatibility
    if (candidate.communication_rating) {
      const commScore = Math.min(0.2, (candidate.communication_rating - 3.0) * 0.1);
      score += commScore;
    }

    // Work style preferences (from ML features)
    const workStyleFit = this.assessWorkStyleFit(candidate, job);
    score += workStyleFit * 0.2;

    // Language compatibility
    const languageFit = this.calculateLanguageCompatibility(candidate, job);
    score += languageFit * 0.15;

    // Timezone compatibility
    const timezoneFit = this.calculateTimezoneCompatibility(candidate, job);
    score += timezoneFit * 0.1;

    // Values alignment (if data available)
    const valuesAlignment = this.assessValuesAlignment(candidate, job);
    score += valuesAlignment * 0.15;

    return Math.min(1.0, Math.max(0.2, score));
  }

  /**
   * Calculate communication effectiveness score
   */
  calculateCommunicationScore(candidate, behaviorData) {
    let score = 0.5; // Base communication score

    // Communication rating
    if (candidate.communication_rating) {
      score += ((candidate.communication_rating - 3.0) / 2.0) * 0.4;
    }

    // Response time factor
    if (candidate.response_time_avg) {
      const responseScore = Math.max(0, 1 - (candidate.response_time_avg / (24 * 3600))); // 24 hour max
      score += responseScore * 0.3;
    }

    // Message activity patterns
    if (behaviorData && behaviorData.recent_messages !== undefined) {
      const activityScore = Math.min(0.2, behaviorData.recent_messages * 0.01);
      score += activityScore;
    }

    // Language proficiency (if available in ML features)
    const languageScore = this.assessLanguageProficiency(candidate);
    score += languageScore * 0.1;

    return Math.min(1.0, Math.max(0.1, score));
  }

  /**
   * Calculate weighted composite score from individual scores
   */
  calculateCompositeScore(scores, weights) {
    const {
      skillMatchScore,
      compatibilityScore,
      successProbability,
      retentionScore,
      performanceScore,
      culturalFitScore,
      communicationScore
    } = scores;

    const compositeScore = 
      (skillMatchScore * weights.skillMatch) +
      (compatibilityScore * weights.compatibility) +
      (successProbability * weights.success) +
      (retentionScore * weights.retention) +
      (performanceScore * weights.performance) +
      (culturalFitScore * weights.culturalFit) +
      (communicationScore * weights.communication);

    return Math.min(1.0, Math.max(0.0, compositeScore));
  }

  /**
   * Get ML model weights from database or use defaults
   */
  async getModelWeights() {
    try {
      const { data: modelData, error } = await supabase
        .from('ml_model_performance')
        .select('feature_weights')
        .eq('model_type', 'compatibility_prediction')
        .eq('is_active', true)
        .limit(1);

      if (error) {
        // Handle specific error codes
        if (error.code === '406' || error.status === 406) {
          console.debug('[ML] Model performance query not acceptable - using defaults');
          return this.getDefaultWeights();
        }
        if (error.code === '42501') {
          console.debug('[ML] No access to model performance table - using defaults');
          return this.getDefaultWeights();
        }
        throw error;
      }

      if (modelData && modelData.length > 0 && modelData[0].feature_weights) {
        const weights = modelData[0].feature_weights;
        return {
          skillMatch: weights.skillMatch || 0.25,
          compatibility: weights.compatibility || 0.20,
          success: weights.success || 0.20,
          retention: weights.retention || 0.15,
          performance: weights.performance || 0.10,
          culturalFit: weights.culturalFit || 0.05,
          communication: weights.communication || 0.05,
          modelAccuracy: weights.modelAccuracy || 0.85
        };
      }
    } catch (error) {
      console.debug('[ML] Using default weights due to error:', error.message);
    }

    return this.getDefaultWeights();
  }

  /**
   * Get default model weights
   */
  getDefaultWeights() {
    return {
      skillMatch: 0.25,
      compatibility: 0.20,
      success: 0.20,
      retention: 0.15,
      performance: 0.10,
      culturalFit: 0.05,
      communication: 0.05,
      modelAccuracy: 0.85
    };
  }

  /**
   * Check if user is admin with caching to avoid repeated DB calls
   */
  async isUserAdmin(userId) {
    const now = Date.now();
    const cached = this.userRoleCache.get(userId);

    // Return cached result if still valid
    if (cached && (now - cached.timestamp) < this.roleCacheExpiry) {
      return cached.isAdmin;
    }

    try {
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', userId)
        .single();

      const isAdmin = userProfile?.role === 'admin';
      
      // Cache the result
      this.userRoleCache.set(userId, {
        isAdmin,
        timestamp: now
      });

      return isAdmin;
    } catch (error) {
      console.debug('[ML] Error checking admin status:', error.message);
      return false;
    }
  }

  /**
   * Get candidate ML features from database
   */
  async getCandidateMLFeatures(candidateId) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(`
          id, skills, experience_years, hourly_rate, rating, total_reviews,
          ml_features, behavioral_score, activity_score, response_time_avg,
          success_rate, retention_rate, communication_rating, work_quality_rating,
          location, languages, availability_status, created_at
        `)
        .eq('id', candidateId)
        .eq('role', 'va')
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[ML] Error fetching candidate features:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get job ML features from database
   */
  async getJobMLFeatures(jobId) {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select(`
          id, title, description, skills_required, hourly_rate_min, hourly_rate_max,
          min_experience_years, job_type, experience_level, is_remote, location,
          ml_requirements, difficulty_score, complexity_rating,
          cultural_requirements, success_criteria
        `)
        .eq('id', jobId)
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[ML] Error fetching job features:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get behavioral data for candidate
   */
  async getBehavioralData(candidateId) {
    try {
      const { data, error } = await supabase
        .from('user_behavior_analytics')
        .select('behavior_type, timestamp, metadata')
        .eq('user_id', candidateId)
        .gte('timestamp', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
        .order('timestamp', { ascending: false });

      if (error) {
        // If table doesn't exist, return empty data
        if (error.code === '42P01') {
          console.debug('[ML] user_behavior_analytics table does not exist - returning empty behavioral data');
          return { success: true, data: {} };
        }
        throw error;
      }

      // Process behavioral data
      const processedData = this.processBehavioralData(data || []);

      return { success: true, data: processedData };
    } catch (error) {
      console.error('[ML] Error fetching behavioral data:', error);
      return { success: false, data: {} };
    }
  }

  /**
   * Process raw behavioral data into ML features
   */
  processBehavioralData(rawData) {
    const processed = {
      recent_applications: 0,
      recent_messages: 0,
      profile_views: 0,
      response_patterns: {},
      activity_distribution: {}
    };

    for (const record of rawData) {
      switch (record.behavior_type) {
        case 'job_application':
          processed.recent_applications++;
          break;
        case 'message_sent':
        case 'message_received':
          processed.recent_messages++;
          break;
        case 'profile_view':
          processed.profile_views++;
          break;
      }

      // Track daily activity distribution
      const day = new Date(record.timestamp).toDateString();
      processed.activity_distribution[day] = (processed.activity_distribution[day] || 0) + 1;
    }

    return processed;
  }

  /**
   * Store ML results in database
   */
  async storeMLResults(result) {
    try {
      // Check if we have an authenticated user
      const { data: currentUser } = await supabase.auth.getUser();
      if (!currentUser?.user) {
        console.debug('[ML] Skipping ML results storage - no authenticated user');
        return;
      }

      // Check if current user is admin (required for ml_scoring_results table)
      const isAdmin = await this.isUserAdmin(currentUser.user.id);
      if (!isAdmin) {
        console.debug('[ML] Skipping ML results storage - admin access required');
        return;
      }

      const { error } = await supabase
        .from('ml_scoring_results')
        .upsert(result, {
          onConflict: 'candidate_id, job_id, employer_id'
        });

      if (error) {
        // Handle RLS policy violations gracefully
        if (error.code === '42501') {
          console.debug('[ML] Skipping ML results storage - RLS policy restriction');
          return;
        }
        throw error;
      }

      console.debug('[ML] Stored ML results successfully');
    } catch (error) {
      console.warn('[ML] Error storing ML results (non-critical):', error.message || error);
    }
  }

  /**
   * Cache score for fast retrieval
   */
  async cacheScore(candidateId, jobId, employerId, result) {
    try {
      // Validate required parameters
      if (!candidateId || !jobId || !employerId || !result) {
        console.debug('[ML] Skipping cache - missing required parameters');
        return;
      }

      // Check if we have an authenticated user
      const { data: currentUser } = await supabase.auth.getUser();
      if (!currentUser?.user) {
        console.debug('[ML] Skipping cache storage - no authenticated user');
        return;
      }

      // Check if current user is admin (required for match_suggestions_cache table)
      const isAdmin = await this.isUserAdmin(currentUser.user.id);
      if (!isAdmin) {
        console.debug('[ML] Skipping cache storage - admin access required');
        return;
      }

      // Validate that referenced records exist to avoid foreign key violations
      const validationResults = await Promise.allSettled([
        this.checkRecordExists('user_profiles', candidateId),
        this.checkRecordExists('user_profiles', employerId),  
        this.checkRecordExists('jobs', jobId)
      ]);

      const [candidateCheck, employerCheck, jobCheck] = validationResults;
      const candidateExists = candidateCheck.status === 'fulfilled' && candidateCheck.value;
      const employerExists = employerCheck.status === 'fulfilled' && employerCheck.value;
      const jobExists = jobCheck.status === 'fulfilled' && jobCheck.value;

      if (!candidateExists || !employerExists || !jobExists) {
        console.debug('[ML] Skipping cache - referenced records do not exist', {
          candidateExists,
          employerExists, 
          jobExists
        });
        return;
      }

      const cacheKey = `ml_score_${candidateId}_${jobId}_${employerId}`;
      const expiresAt = new Date(Date.now() + this.cacheExpiry * 1000);

      const cacheData = {
        employer_id: employerId,
        candidate_id: candidateId,
        job_id: jobId,
        match_score: result.composite_score || 0,
        recommendation_rank: 1, // Will be updated by ranking algorithm
        match_reasons: this.generateMatchReasons(result) || [],
        ai_insights: this.generateAIInsights(result) || {},
        cache_key: cacheKey,
        expires_at: expiresAt.toISOString(),
        last_updated: new Date().toISOString()
      };

      // Try to insert first, if it fails due to duplicate, then update
      let { error } = await supabase
        .from('match_suggestions_cache')
        .insert(cacheData);

      // If insert failed due to duplicate cache_key, try to update existing record
      if (error && error.message && error.message.includes('duplicate')) {
        const { error: updateError } = await supabase
          .from('match_suggestions_cache')
          .update({
            match_score: cacheData.match_score,
            recommendation_rank: cacheData.recommendation_rank,
            match_reasons: cacheData.match_reasons,
            ai_insights: cacheData.ai_insights,
            expires_at: cacheData.expires_at,
            last_updated: cacheData.last_updated
          })
          .eq('cache_key', cacheKey);
        
        error = updateError;
      }

      if (error) {
        // Handle specific database errors gracefully
        if (error.code === '42501') {
          console.debug('[ML] Skipping cache storage - RLS policy restriction');
          return;
        }
        if (error.code === '23503') {
          console.debug('[ML] Skipping cache storage - foreign key constraint violation');
          return;
        }
        if (error.code === '23505') {
          console.debug('[ML] Cache conflict resolved via upsert');
          return;
        }
        if (error.code === '400' || error.status === 400) {
          console.debug('[ML] Skipping cache storage - bad request data format');
          return;
        }
        console.warn('[ML] Cache storage failed (non-critical):', error.message);
        return;
      }

      console.debug('[ML] Cached score successfully');
    } catch (error) {
      console.warn('[ML] Error caching score (non-critical):', error.message);
    }
  }

  /**
   * Get cached score if available and not expired
   */
  async getCachedScore(candidateId, jobId, employerId) {
    try {
      // Check if we have an authenticated user
      const { data: currentUser } = await supabase.auth.getUser();
      if (!currentUser?.user) {
        console.debug('[ML] Skipping cache retrieval - no authenticated user');
        return null;
      }

      // Check if current user is admin (required for match_suggestions_cache table)
      const isAdmin = await this.isUserAdmin(currentUser.user.id);
      if (!isAdmin) {
        console.debug('[ML] Skipping cache retrieval - admin access required');
        return null;
      }

      const cacheKey = `ml_score_${candidateId}_${jobId}_${employerId}`;
      const now = new Date();

      const { data, error } = await supabase
        .from('match_suggestions_cache')
        .select('*')
        .eq('cache_key', cacheKey)
        .eq('employer_id', employerId)
        .gte('expires_at', now.toISOString())
        .limit(1);

      if (error) {
        // Handle specific errors gracefully
        if (error.code === '406' || error.status === 406) {
          console.debug('[ML] Cache query not acceptable - skipping cache');
          return null;
        }
        if (error.code === '42501') {
          console.debug('[ML] No cache access - RLS policy restriction');
          return null;
        }
        console.debug('[ML] Cache query error:', error.message);
        return null;
      }

      if (!data || data.length === 0) {
        console.debug('[ML] No valid cache found');
        return null;
      }

      const cacheEntry = data[0];

      // Also get the detailed ML results
      const { data: mlData } = await supabase
        .from('ml_scoring_results')
        .select('*')
        .eq('candidate_id', candidateId)
        .eq('job_id', jobId)
        .eq('employer_id', employerId)
        .limit(1);

      if (mlData && mlData.length > 0) {
        return {
          ...mlData[0],
          match_reasons: cacheEntry.match_reasons,
          ai_insights: cacheEntry.ai_insights,
          cached: true
        };
      }

      // Return cached data if no detailed ML results found
      return {
        composite_score: cacheEntry.match_score || 0,
        match_reasons: cacheEntry.match_reasons || [],
        ai_insights: cacheEntry.ai_insights || {},
        cached: true,
        source: 'cache_only'
      };
    } catch (error) {
      console.warn('[ML] Error getting cached score:', error.message);
      return null;
    }
  }

  /**
   * Check if a record exists in a table (for foreign key validation)
   */
  async checkRecordExists(tableName, recordId) {
    try {
      const { data, error } = await supabase
        .from(tableName)
        .select('id')
        .eq('id', recordId)
        .limit(1);

      if (error) {
        console.debug(`[ML] Error checking ${tableName} existence:`, error.message);
        return false;
      }

      return data && data.length > 0;
    } catch (error) {
      console.debug(`[ML] Error checking ${tableName} existence:`, error.message);
      return false;
    }
  }

  // Helper methods for scoring calculations
  normalizeSkills(skills) {
    if (!Array.isArray(skills)) {
      if (typeof skills === 'string') {
        try {
          skills = JSON.parse(skills);
        } catch {
          skills = skills.split(',').map(s => s.trim());
        }
      } else {
        return [];
      }
    }
    
    // Handle different skill formats (string, object, etc.)
    return skills.map(skill => {
      if (typeof skill === 'string') {
        return skill.toLowerCase().trim();
      } else if (typeof skill === 'object' && skill !== null) {
        // Handle skill objects that have a 'name' property
        if (skill.name) {
          return skill.name.toLowerCase().trim();
        } else if (skill.skill) {
          return skill.skill.toLowerCase().trim();
        } else if (skill.title) {
          return skill.title.toLowerCase().trim();
        }
        // If it's an object but doesn't have expected properties, try to convert to string
        return String(skill).toLowerCase().trim();
      } else {
        // Handle other types by converting to string
        return String(skill).toLowerCase().trim();
      }
    }).filter(skill => skill && skill.length > 0); // Filter out empty strings
  }

  /**
   * Normalize languages to handle different data formats
   */
  normalizeLanguages(languages) {
    if (!languages) return ['English']; // Default fallback
    
    // Handle array format
    if (Array.isArray(languages)) {
      if (languages.length === 0) return ['English'];
      
      // Handle different language formats (string, object, etc.)
      return languages.map(lang => {
        if (typeof lang === 'string') {
          return lang.trim();
        } else if (typeof lang === 'object' && lang !== null) {
          // Handle language objects that have various properties
          if (lang.name) {
            return lang.name.trim();
          } else if (lang.language) {
            return lang.language.trim();
          } else if (lang.lang) {
            return lang.lang.trim();
          } else if (lang.title) {
            return lang.title.trim();
          }
          // If it's an object but doesn't have expected properties, try to convert to string
          return String(lang).trim();
        } else {
          // Handle other types by converting to string
          return String(lang).trim();
        }
      }).filter(lang => lang && lang.length > 0); // Filter out empty strings
    }
    
    // Handle single language value
    if (typeof languages === 'string') {
      try {
        // Try to parse as JSON array
        const parsed = JSON.parse(languages);
        if (Array.isArray(parsed)) {
          return this.normalizeLanguages(parsed);
        }
      } catch {
        // If not JSON, treat as comma-separated string
        return languages.split(',').map(l => l.trim()).filter(l => l);
      }
    }
    
    // Handle object format
    if (typeof languages === 'object' && languages !== null) {
      if (languages.name) {
        return [languages.name.trim()];
      } else if (languages.language) {
        return [languages.language.trim()];
      } else if (languages.lang) {
        return [languages.lang.trim()];
      }
    }
    
    // Fallback
    return ['English'];
  }

  getSkillCategory(skill, categories) {
    const skillLower = skill.toLowerCase();
    for (const [category, keywords] of Object.entries(categories)) {
      if (keywords.some(keyword => skillLower.includes(keyword))) {
        return category;
      }
    }
    return null;
  }

  calculateSemanticSimilarity(skill1, skill2) {
    // Simplified semantic similarity - in production, use proper NLP
    const s1 = skill1.toLowerCase();
    const s2 = skill2.toLowerCase();
    
    if (s1 === s2) return 1.0;
    if (s1.includes(s2) || s2.includes(s1)) return 0.8;
    
    // Simple character-based similarity
    const maxLen = Math.max(s1.length, s2.length);
    const minLen = Math.min(s1.length, s2.length);
    
    if (maxLen === 0) return 0;
    
    let matches = 0;
    for (let i = 0; i < minLen; i++) {
      if (s1[i] === s2[i]) matches++;
    }
    
    return (matches / maxLen) * 0.5; // Cap at 0.5 for character similarity
  }

  calculateRateCompatibility(candidateRate, minRate, maxRate) {
    if (!candidateRate || (!minRate && !maxRate)) return 0.7;
    
    const min = minRate || 1;
    const max = maxRate || min * 2;
    
    if (candidateRate >= min && candidateRate <= max) {
      return 1.0;
    } else if (candidateRate < min) {
      return Math.max(0.3, 1 - ((min - candidateRate) / min));
    } else {
      return Math.max(0.3, 1 - ((candidateRate - max) / max));
    }
  }

  calculateExperienceCompatibility(candidateExp, requiredExp) {
    if (candidateExp >= requiredExp) {
      return Math.min(1.0, 0.8 + (candidateExp - requiredExp) * 0.05);
    } else {
      return Math.max(0.2, candidateExp / requiredExp);
    }
  }

  calculateWorkStyleCompatibility(candidate, job) {
    let score = 0.6;
    
    // Remote work compatibility
    if (job.is_remote && candidate.location) {
      score += 0.2; // Bonus for remote positions
    }
    
    // Job type compatibility (based on candidate preferences in ml_features)
    const mlFeatures = candidate.ml_features || {};
    if (mlFeatures.preferred_job_types && job.job_type) {
      if (mlFeatures.preferred_job_types.includes(job.job_type)) {
        score += 0.2;
      }
    }
    
    return Math.min(1.0, score);
  }

  calculateComplexityCompatibility(candidate, job) {
    const candidateLevel = this.getExperienceLevel(candidate.experience_years || 0);
    const jobComplexity = job.complexity_rating || 3;
    
    const levelToComplexity = {
      'entry': 1,
      'junior': 2,
      'mid': 3,
      'senior': 4,
      'expert': 5
    };
    
    const candidateComplexity = levelToComplexity[candidateLevel] || 3;
    const diff = Math.abs(candidateComplexity - jobComplexity);
    
    return Math.max(0.2, 1.0 - (diff * 0.2));
  }

  calculateAvailabilityCompatibility(candidate, _job) {
    if (candidate.availability_status === 'available') return 1.0;
    if (candidate.availability_status === 'busy') return 0.5;
    if (candidate.availability_status === 'unavailable') return 0.1;
    return 0.7; // Default if not specified
  }

  calculateDomainCompatibility(candidate, job) {
    // This would typically analyze industry/domain keywords
    // Simplified implementation
    const candidateSkills = this.normalizeSkills(candidate.skills || []);
    const jobSkills = this.normalizeSkills(job.skills_required || []);
    
    const sharedSkills = candidateSkills.filter(skill => 
      jobSkills.some(jobSkill => skill.includes(jobSkill) || jobSkill.includes(skill))
    );
    
    return Math.min(1.0, 0.4 + (sharedSkills.length * 0.15));
  }

  calculateComplexityExperienceMatch(candidate, job) {
    const experience = candidate.experience_years || 0;
    const complexity = job.complexity_rating || 3;
    
    const idealExperience = complexity * 1.5; // Rough mapping
    const diff = Math.abs(experience - idealExperience);
    
    return Math.max(0.1, 1.0 - (diff * 0.1));
  }

  calculateProfileCompleteness(candidate) {
    let completeness = 0;
    let totalFields = 0;
    
    const fields = [
      'skills', 'experience_years', 'hourly_rate', 'location',
      'bio', 'portfolio_url', 'languages'
    ];
    
    for (const field of fields) {
      totalFields++;
      if (candidate[field] && candidate[field] !== '') {
        completeness++;
      }
    }
    
    return totalFields > 0 ? completeness / totalFields : 0.5;
  }

  calculateBehavioralStability(behaviorData) {
    // Analyze consistency in activity patterns
    const activities = Object.values(behaviorData.activity_distribution || {});
    if (activities.length === 0) return 0.5;
    
    const mean = activities.reduce((a, b) => a + b, 0) / activities.length;
    const variance = activities.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / activities.length;
    const stdDev = Math.sqrt(variance);
    
    // Lower standard deviation indicates more consistent behavior
    return Math.max(0.1, 1.0 - (stdDev / mean));
  }

  calculateSkillsToRequirementsRatio(candidate, job) {
    const candidateSkills = this.normalizeSkills(candidate.skills || []);
    const requiredSkills = this.normalizeSkills(job.skills_required || []);
    
    if (requiredSkills.length === 0) return 0.7;
    
    const matchedSkills = requiredSkills.filter(reqSkill =>
      candidateSkills.some(candSkill => 
        candSkill.includes(reqSkill) || reqSkill.includes(candSkill)
      )
    );
    
    return matchedSkills.length / requiredSkills.length;
  }

  calculateExperienceAdequacy(candidate, job) {
    const minExp = job.min_experience_years || 0;
    const candidateExp = candidate.experience_years || 0;
    
    if (candidateExp >= minExp * 1.5) return 1.0; // Over-qualified
    if (candidateExp >= minExp) return 0.9; // Perfectly qualified
    if (candidateExp >= minExp * 0.75) return 0.7; // Mostly qualified
    return Math.max(0.2, candidateExp / minExp); // Under-qualified
  }

  assessPortfolioQuality(candidate) {
    // Simplified portfolio assessment - would analyze actual portfolio in production
    const mlFeatures = candidate.ml_features || {};
    return mlFeatures.portfolio_score || 0.5;
  }

  calculateSpecializationMatch(candidate, job) {
    // Analyze if candidate's specialization matches job requirements
    const candidateSkills = this.normalizeSkills(candidate.skills || []);
    const jobTitle = (job.title || '').toLowerCase();
    
    let specializationScore = 0.5;
    
    // Check for specialized skills mentioned in job title
    for (const skill of candidateSkills) {
      if (jobTitle.includes(skill)) {
        specializationScore += 0.1;
      }
    }
    
    return Math.min(1.0, specializationScore);
  }

  assessWorkStyleFit(candidate, job) {
    // Analyze work style preferences from ML features
    const candidateML = candidate.ml_features || {};
    const jobML = job.ml_requirements || {};
    
    let fitScore = 0.5;
    
    // Communication style match
    if (candidateML.communication_style && jobML.preferred_communication_style) {
      if (candidateML.communication_style === jobML.preferred_communication_style) {
        fitScore += 0.2;
      }
    }
    
    // Work pace match
    if (candidateML.work_pace && jobML.required_work_pace) {
      const paceMatch = Math.abs(candidateML.work_pace - jobML.required_work_pace);
      fitScore += Math.max(0, 0.2 - (paceMatch * 0.1));
    }
    
    return Math.min(1.0, fitScore);
  }

  calculateLanguageCompatibility(candidate, job) {
    const candidateLanguages = this.normalizeLanguages(candidate.languages || ['English']);
    const jobLanguages = this.normalizeLanguages(job.ml_requirements?.required_languages || ['English']);
    
    const sharedLanguages = candidateLanguages.filter(lang =>
      jobLanguages.some(jobLang => lang.toLowerCase() === jobLang.toLowerCase())
    );
    
    return sharedLanguages.length > 0 ? Math.min(1.0, sharedLanguages.length / jobLanguages.length) : 0.3;
  }

  calculateTimezoneCompatibility(candidate, job) {
    // Simplified timezone compatibility - would use actual timezone data in production
    if (job.is_remote) return 1.0;
    
    const candidateLocation = (candidate.location || '').toLowerCase();
    const jobLocation = (job.location || '').toLowerCase();
    
    if (candidateLocation.includes(jobLocation) || jobLocation.includes(candidateLocation)) {
      return 1.0;
    }
    
    return 0.6; // Moderate compatibility as default
  }

  assessValuesAlignment(candidate, job) {
    // Analyze values alignment from cultural requirements
    const candidateML = candidate.ml_features || {};
    const jobCultural = job.cultural_requirements || {};
    
    let alignment = 0.6; // Base alignment
    
    // Company culture match
    if (candidateML.cultural_preferences && jobCultural.company_culture) {
      const preferences = candidateML.cultural_preferences;
      const culture = jobCultural.company_culture;
      
      // Simple keyword matching - would be more sophisticated in production
      const matches = Object.keys(preferences).filter(key => 
        culture[key] && Math.abs(preferences[key] - culture[key]) < 0.3
      );
      
      alignment += matches.length * 0.1;
    }
    
    return Math.min(1.0, alignment);
  }

  assessLanguageProficiency(candidate) {
    const mlFeatures = candidate.ml_features || {};
    return mlFeatures.language_proficiency_score || 0.7;
  }

  getExperienceLevel(years) {
    if (years < 1) return 'entry';
    if (years < 3) return 'junior';
    if (years < 5) return 'mid';
    if (years < 10) return 'senior';
    return 'expert';
  }

  calculateConfidenceLevel({ dataQuality, modelAccuracy, featureCompleteness }) {
    const confidence = (dataQuality + modelAccuracy + featureCompleteness) / 3;
    
    if (confidence >= 0.9) return 'very_high';
    if (confidence >= 0.8) return 'high';
    if (confidence >= 0.6) return 'medium';
    if (confidence >= 0.4) return 'low';
    return 'very_low';
  }

  assessDataQuality(candidateData, jobData) {
    let quality = 0.5;
    
    // Check candidate data completeness
    const candidateFields = ['skills', 'experience_years', 'rating', 'hourly_rate'];
    const candidateCompleteness = candidateFields.filter(field => 
      candidateData[field] !== undefined && candidateData[field] !== null
    ).length / candidateFields.length;
    
    // Check job data completeness
    const jobFields = ['skills_required', 'min_experience_years', 'hourly_rate_min'];
    const jobCompleteness = jobFields.filter(field => 
      jobData[field] !== undefined && jobData[field] !== null
    ).length / jobFields.length;
    
    quality = (candidateCompleteness + jobCompleteness) / 2;
    
    return quality;
  }

  calculateFeatureCompleteness(candidateData) {
    const requiredFeatures = [
      'skills', 'experience_years', 'hourly_rate', 'rating',
      'location', 'availability_status'
    ];
    
    const completedFeatures = requiredFeatures.filter(feature => 
      candidateData[feature] !== undefined && 
      candidateData[feature] !== null && 
      candidateData[feature] !== ''
    ).length;
    
    return completedFeatures / requiredFeatures.length;
  }

  generateFeatureImportance(scores) {
    // Calculate relative importance of each factor
    const total = Object.values(scores).reduce((sum, score) => sum + score, 0);
    
    return {
      skill_matching: (scores.skillMatchScore / total * 100).toFixed(1),
      compatibility: (scores.compatibilityScore / total * 100).toFixed(1),
      success_probability: (scores.successProbability / total * 100).toFixed(1),
      retention_likelihood: (scores.retentionScore / total * 100).toFixed(1),
      performance_potential: (scores.performanceScore / total * 100).toFixed(1),
      cultural_fit: (scores.culturalFitScore / total * 100).toFixed(1),
      communication: (scores.communicationScore / total * 100).toFixed(1)
    };
  }

  generateMatchReasons(result) {
    const reasons = [];
    
    if (!result || typeof result !== 'object') {
      return reasons;
    }
    
    if ((result.skill_match_score || 0) > 0.8) {
      reasons.push('Excellent skill alignment with job requirements');
    } else if ((result.skill_match_score || 0) > 0.6) {
      reasons.push('Good skill match for this position');
    }
    
    if ((result.success_probability || 0) > 0.8) {
      reasons.push('High likelihood of project success');
    }
    
    if ((result.retention_score || 0) > 0.7) {
      reasons.push('Strong retention potential based on profile');
    }
    
    if (result.cultural_fit_score > 0.7) {
      reasons.push('Good cultural fit with work requirements');
    }
    
    if (result.performance_score > 0.8) {
      reasons.push('Predicted high performance output');
    }
    
    if (reasons.length === 0) {
      reasons.push('Moderate compatibility with growth potential');
    }
    
    return reasons.slice(0, 4); // Limit to top 4 reasons
  }

  generateAIInsights(result) {
    if (!result || typeof result !== 'object') {
      return {
        composite_score_explanation: 'No scoring data available',
        top_strengths: [],
        potential_concerns: [],
        recommendation: 'Unable to generate recommendation',
        confidence_factors: {
          data_quality: 'low',
          model_version: 'unknown',
          calculation_method: 'Advanced ML algorithms with behavioral analysis'
        }
      };
    }

    return {
      composite_score_explanation: `Overall match score of ${((result.composite_score || 0) * 100).toFixed(1)}% based on comprehensive ML analysis`,
      top_strengths: this.identifyTopStrengths(result),
      potential_concerns: this.identifyPotentialConcerns(result),
      recommendation: this.generateRecommendation(result),
      confidence_factors: {
        data_quality: result.overall_confidence || 'medium',
        model_version: result.model_version || 'v1.0',
        calculation_method: 'Advanced ML algorithms with behavioral analysis'
      }
    };
  }

  identifyTopStrengths(result) {
    const strengths = [];
    const scores = {
      'Skill Matching': result.skill_match_score,
      'Compatibility': result.compatibility_score,
      'Success Probability': result.success_probability,
      'Retention Potential': result.retention_score,
      'Performance Prediction': result.performance_score,
      'Cultural Fit': result.cultural_fit_score,
      'Communication': result.communication_score
    };
    
    const sortedScores = Object.entries(scores)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3);
    
    for (const [strength, score] of sortedScores) {
      if (score > 0.7) {
        strengths.push(`${strength}: ${(score * 100).toFixed(1)}%`);
      }
    }
    
    return strengths;
  }

  identifyPotentialConcerns(result) {
    const concerns = [];
    
    if (result.skill_match_score < 0.5) {
      concerns.push('Limited skill alignment - may require additional training');
    }
    
    if (result.retention_score < 0.4) {
      concerns.push('Lower retention probability - consider engagement strategies');
    }
    
    if (result.communication_score < 0.5) {
      concerns.push('Communication efficiency may need attention');
    }
    
    if (result.overall_confidence === 'low' || result.overall_confidence === 'very_low') {
      concerns.push('Limited data available for accurate prediction');
    }
    
    return concerns.slice(0, 3); // Limit to top 3 concerns
  }

  generateRecommendation(result) {
    const score = result.composite_score;
    
    if (score >= 0.9) {
      return 'Highly recommended - Exceptional match with strong success indicators';
    } else if (score >= 0.8) {
      return 'Strongly recommended - Very good match with high success potential';
    } else if (score >= 0.7) {
      return 'Recommended - Good match with solid compatibility indicators';
    } else if (score >= 0.6) {
      return 'Consider with evaluation - Moderate match with some alignment';
    } else if (score >= 0.5) {
      return 'Proceed with caution - Limited compatibility indicators';
    } else {
      return 'Not recommended - Significant compatibility concerns identified';
    }
  }

  /**
   * Batch processing for multiple candidates
   */
  async batchCalculateMLScores(candidateIds, jobId, employerId) {
    const results = [];
    const batchSize = 5; // Process in batches to avoid overwhelming the system
    
    for (let i = 0; i < candidateIds.length; i += batchSize) {
      const batch = candidateIds.slice(i, i + batchSize);
      
      const batchPromises = batch.map(candidateId =>
        this.calculateMLMatchScore(candidateId, jobId, employerId)
      );
      
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
      
      // Small delay between batches
      if (i + batchSize < candidateIds.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    return results;
  }

  /**
   * Update model performance metrics
   */
  async updateModelPerformance(modelType, metrics) {
    try {
      const { error } = await supabase
        .from('ml_model_performance')
        .update({
          accuracy_score: metrics.accuracy,
          precision_score: metrics.precision,
          recall_score: metrics.recall,
          f1_score: metrics.f1,
          evaluation_date: new Date().toISOString()
        })
        .eq('model_type', modelType)
        .eq('is_active', true);

      if (error) throw error;

      console.log(`[ML] Updated performance metrics for ${modelType}`);
    } catch (error) {
      console.error('[ML] Error updating model performance:', error);
    }
  }

  /**
   * Track user behavior for ML training
   */
  async trackBehavior(userId, behaviorType, metadata = {}) {
    try {
      // Check if user_behavior_analytics table exists and if we can track behavior
      const { data: currentUser } = await supabase.auth.getUser();
      
      // Only track behavior if:
      // 1. User is authenticated
      // 2. We're tracking behavior for the current user (RLS policy)
      if (!currentUser?.user || currentUser.user.id !== userId) {
        console.debug('[ML] Skipping behavior tracking - not current user or unauthenticated');
        return;
      }

      const { error } = await supabase
        .from('user_behavior_analytics')
        .insert({
          user_id: userId,
          behavior_type: behaviorType,
          metadata: metadata,
          timestamp: new Date().toISOString()
        });

      if (error) {
        // If table doesn't exist or RLS policy violation, handle gracefully
        if (error.code === '42P01') {
          console.debug('[ML] user_behavior_analytics table does not exist - skipping behavior tracking');
          return;
        }
        if (error.code === '42501') {
          console.debug('[ML] RLS policy violation - skipping behavior tracking for user:', userId);
          return;
        }
        throw error;
      }
    } catch (error) {
      console.warn('[ML] Error tracking behavior:', error.message);
    }
  }
}

// Export singleton instance
export const mlScoringService = new MLScoringService();

// Export individual methods for direct use
export const {
  calculateMLMatchScore,
  batchCalculateMLScores,
  updateModelPerformance,
  trackBehavior
} = mlScoringService;
